/**
 * The client, v2's only door to the server (API_ACCESS.md §3.4). `call` does five things, in order:
 * asks the gate, sends through the transport, classifies the answer, learns from a server refusal,
 * and resolves, never throwing and never toasting. v2 says the outcome itself.
 *
 * It depends on two ports and nothing else: the `Transport`, and the session's access (the
 * capability engine's `canCall`, `suspend`, `subscribe` and `facts`). A test binds fakes.
 */
import type { Capabilities } from "../capabilities/engine";
import { classify } from "./classify";
import { urlFor, type Route } from "./route";
import type { ApiClient, ApiReport, ApiResult, RawAnswer, Transport } from "./types";

/** what the client needs of the capability engine */
export type AccessPort = Pick<Capabilities, "canCall" | "suspend" | "subscribe" | "facts">;

export interface ClientDeps {
  transport: Transport;
  access: AccessPort;
  /** QR2 (decided 2026-10-06): `console.warn`; each app may bind its event log at boot */
  report?: (event: ApiReport) => void;
}

const isDevelopment = () => typeof process !== "undefined" && process.env?.NODE_ENV !== "production";

export const warnReport = (event: ApiReport): void => {
  console.warn(`api: ${event.kind} ${event.route}`, event);
};

export function createApiClient({ transport, access, report = warnReport }: ClientDeps): ApiClient {
  /** a feature refusal is reported once per route and code (§3.6) */
  const restrictedSeen = new Set<string>();

  const send = async (declared: Route<any, any, any>, input: { body?: unknown; query?: unknown; params?: unknown }): Promise<RawAnswer> => {
    try {
      const url = urlFor(declared, (input.params ?? {}) as Record<string, unknown>);
      return await transport.send({ route: declared, url, body: input.body, query: input.query as Record<string, unknown> | undefined });
    } catch (error) {
      // a missing path parameter is a caller's bug: say so in development, and never throw
      if (isDevelopment()) console.warn(`api: ${declared.key} was not sent`, error);
      return { failure: "network" };
    }
  };

  return {
    async call(declared, input = {} as never) {
      const decision = access.canCall([declared]);
      if (!decision.allowed) {
        if (isDevelopment()) report({ kind: "refusedByClient", route: declared.key, decision });
        return { ok: false, error: { kind: "refused", by: "client", decision } };
      }

      const raw = await send(declared, input as { body?: unknown; query?: unknown; params?: unknown });
      const result: ApiResult<any> = classify(raw, declared);

      if (!result.ok && result.error.kind === "network" && !access.facts().env.online) {
        return { ok: false, error: { kind: "offline" } };
      }
      if (!result.ok && result.error.kind === "refused") {
        const { subject } = access.facts();
        if (access.suspend(declared.key)) {
          report({ kind: "refusedByServer", route: declared.key, modules: [...subject.modules], role: subject.role });
        }
      }
      if (!result.ok && result.error.kind === "restricted") {
        const seen = `${declared.key}|${result.error.code}`;
        if (!restrictedSeen.has(seen)) {
          restrictedSeen.add(seen);
          report({ kind: "restricted", route: declared.key, code: result.error.code });
        }
      }
      return result;
    },
    canCall: (...routes) => access.canCall(routes),
    subscribe: (listener) => access.subscribe(listener),
    snapshot: () => access.facts(),
  };
}
